import React, { useEffect, useMemo, useState } from 'react';
import { useAdminOccurrenceEdits } from '@/hooks/useAdminOccurrenceEdits';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/auth/useAuth';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';

const ITEMS_PER_PAGE = 20;

export default function AdminOccurrenceEditsPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const [sortField, setSortField] = useState<'occurrence_date' | 'teacher_code' | 'subject' | 'status' | 'edit_status'>('occurrence_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [riskOnly, setRiskOnly] = useState(false);

  const { query, filteredRows, total, riskMap, recomputeRisks, approveReject, overrideEdit } = useAdminOccurrenceEdits({
    page,
    itemsPerPage: ITEMS_PER_PAGE,
    searchQuery: search,
    statusFilter,
    sortField,
    sortDirection,
    riskOnly,
  });

  useEffect(() => {
    if (query.data?.rows) recomputeRisks(query.data.rows);
  }, [query.data?.rows, recomputeRisks]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / ITEMS_PER_PAGE)), [total]);

  // Approve/Reject dialog state
  const [actionTarget, setActionTarget] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [actionReason, setActionReason] = useState('');

  // Override dialog state
  const [overrideTarget, setOverrideTarget] = useState<string | null>(null);
  const [ovStatus, setOvStatus] = useState<'SCHEDULED' | 'TEACHER_LEAVE' | 'STUDENT_LEAVE' | 'STUDENT_ABSENT'>('SCHEDULED');
  const [ovStart, setOvStart] = useState('');
  const [ovEnd, setOvEnd] = useState('');
  const [ovNote, setOvNote] = useState('');

  const timeOptions = useMemo(() => {
    const arr: string[] = [];
    for (let h = 6; h <= 24; h++) {
      for (const m of [0, 30]) {
        if (h === 24 && m > 0) continue;
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        arr.push(`${hh}:${mm}`);
      }
    }
    return arr;
  }, []);

  const onSearch = () => {
    setPage(1);
    setSearch(pendingSearch.trim());
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-6">คำขอแก้ไขคาบสอน (ครู)</h1>

            <div className="space-y-4">
              <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1">
          <Label>ค้นหา (รหัสครู/วิชา/สถานที่)</Label>
          <div className="flex gap-2">
            <Input value={pendingSearch} onChange={(e)=>setPendingSearch(e.target.value)} placeholder="ค้นหา..." />
            <Button onClick={onSearch}>ค้นหา</Button>
          </div>
        </div>
        <div>
          <Label>สถานะคำขอ</Label>
          <Select value={statusFilter} onValueChange={(v)=>{ setStatusFilter(v as any); setPage(1); }}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">ทั้งหมด (มีประวัติแก้ไข)</SelectItem>
              <SelectItem value="PENDING">รออนุมัติ</SelectItem>
              <SelectItem value="APPROVED">อนุมัติแล้ว</SelectItem>
              <SelectItem value="REJECTED">ไม่อนุมัติ</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>เรียงตาม</Label>
          <Select value={sortField} onValueChange={(v)=>setSortField(v as any)}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="occurrence_date">วันที่</SelectItem>
              <SelectItem value="teacher_code">รหัสครู</SelectItem>
              <SelectItem value="subject">วิชา</SelectItem>
              <SelectItem value="status">สถานะคาบ</SelectItem>
              <SelectItem value="edit_status">สถานะแก้ไข</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>ทิศทาง</Label>
          <Select value={sortDirection} onValueChange={(v)=>setSortDirection(v as any)}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">น้อย→มาก</SelectItem>
              <SelectItem value="desc">มาก→น้อย</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <input id="riskOnly" type="checkbox" checked={riskOnly} onChange={(e)=>setRiskOnly(e.target.checked)} />
          <Label htmlFor="riskOnly">แสดงเฉพาะความเสี่ยงชั่วโมงไม่พอ</Label>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 px-3">วันที่</th>
                  <th className="py-2 px-3">เวลา</th>
                  <th className="py-2 px-3">ครู</th>
                  <th className="py-2 px-3">วิชา/สถานที่</th>
                  <th className="py-2 px-3">สถานะคาบ</th>
                  <th className="py-2 px-3">คำขอ</th>
                  <th className="py-2 px-3">ความเสี่ยง</th>
                  <th className="py-2 px-3">การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const pending = r.edit_status === 'PENDING';
                  const time = `${(r.start_time ?? '').toString().slice(0,5)}-${(r.end_time ?? '').toString().slice(0,5)}`;
                  const req = r.edit_request_payload as any;
                  const reqSummary = req ? [
                    req.start_time && `เริ่ม ${req.start_time}`,
                    req.end_time && `จบ ${req.end_time}`,
                    req.status && `สถานะ ${req.status}`,
                  ].filter(Boolean).join(' / ') : '-';
                  const risk = riskMap[r.occurrence_id];
                  return (
                    <tr key={r.occurrence_id} className="border-b">
                      <td className="py-2 px-3 whitespace-nowrap">{r.occurrence_date}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{time}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{r.teacher_code}</td>
                      <td className="py-2 px-3">{r.subject} @ {r.location}</td>
                      <td className="py-2 px-3"><Badge variant="outline">{r.status}</Badge></td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <Badge className={pending ? 'bg-amber-500' : r.edit_status === 'REJECTED' ? 'bg-red-500' : 'bg-green-600'}>
                            {r.edit_status}
                          </Badge>
                          <span className="text-muted-foreground">{reqSummary}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {risk === undefined ? (
                          <span className="text-muted-foreground">-</span>
                        ) : risk ? (
                          <Badge variant="destructive">ชั่วโมงไม่พอ</Badge>
                        ) : (
                          <Badge variant="secondary">ปกติ</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex gap-2">
                          <Button size="sm" disabled={!pending || approveReject.isPending} onClick={()=>{ setActionType('APPROVE'); setActionTarget(r.occurrence_id); }}>อนุมัติ</Button>
                          <Button size="sm" variant="destructive" disabled={!pending || approveReject.isPending} onClick={()=>{ setActionType('REJECT'); setActionTarget(r.occurrence_id); }}>ไม่อนุมัติ</Button>
                          <Button size="sm" variant="outline" disabled={overrideEdit.isPending} onClick={()=>{ setOverrideTarget(r.occurrence_id); setOvStatus(r.status); setOvStart((r.start_time ?? '').slice(0,5)); setOvEnd((r.end_time ?? '').slice(0,5)); setOvNote(''); }}>Override</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-muted-foreground">ไม่พบรายการ</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-4">
            <div className="text-sm text-muted-foreground">หน้าที่ {page} / {totalPages}</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={page<=1 || query.isFetching} onClick={()=>setPage(p=>Math.max(1,p-1))}>ก่อนหน้า</Button>
              <Button variant="outline" disabled={page>=totalPages || query.isFetching} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>ถัดไป</Button>
            </div>
                  </div>
                </CardContent>
              </Card>

      {/* Approve/Reject Dialog */}
      <Dialog open={!!actionTarget} onOpenChange={(o)=>{ if(!o){ setActionTarget(null); setActionReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionType === 'APPROVE' ? 'ยืนยันการอนุมัติคำขอ' : 'ระบุเหตุผลการไม่อนุมัติ'}</DialogTitle>
          </DialogHeader>
          {actionType === 'REJECT' && (
            <div className="space-y-2">
              <Label>เหตุผล</Label>
              <Textarea rows={3} value={actionReason} onChange={(e)=>setActionReason(e.target.value)} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>{ setActionTarget(null); setActionReason(''); }} disabled={approveReject.isPending}>ยกเลิก</Button>
            <Button onClick={async ()=>{
              if (!actionTarget || !user?.email) return;
              await approveReject.mutateAsync({
                occurrenceId: actionTarget,
                reviewer: user.email,
                decision: actionType,
                reason: actionType === 'REJECT' ? actionReason : undefined,
              });
              setActionTarget(null);
              setActionReason('');
            }} disabled={approveReject.isPending}>
              ดำเนินการ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={!!overrideTarget} onOpenChange={(o)=>{ if(!o){ setOverrideTarget(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override คาบสอน</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>เวลาเริ่ม</Label>
              <Select value={ovStart} onValueChange={(v)=>setOvStart(v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="เลือก" /></SelectTrigger>
                <SelectContent>
                  {timeOptions.map((t)=> (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>เวลาสิ้นสุด</Label>
              <Select value={ovEnd} onValueChange={(v)=>setOvEnd(v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="เลือก" /></SelectTrigger>
                <SelectContent>
                  {timeOptions.filter(t=>!ovStart || t>ovStart).map((t)=> (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>สถานะ</Label>
              <Select value={ovStatus} onValueChange={(v)=>setOvStatus(v as any)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="เลือก" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SCHEDULED">สอนตามปกติ</SelectItem>
                  <SelectItem value="STUDENT_LEAVE">นักเรียนลา</SelectItem>
                  <SelectItem value="TEACHER_LEAVE">ครูลา</SelectItem>
                  <SelectItem value="STUDENT_ABSENT">ลากระทันหัน</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label>หมายเหตุ</Label>
            <Textarea rows={3} value={ovNote} onChange={(e)=>setOvNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOverrideTarget(null)} disabled={overrideEdit.isPending}>ยกเลิก</Button>
            <Button onClick={async ()=>{
              if (!overrideTarget || !user?.email) return;
              const payload: Record<string, unknown> = {
                start_time: ovStart || undefined,
                end_time: ovEnd || undefined,
                status: ovStatus || undefined,
                note: ovNote || undefined,
              };
              await overrideEdit.mutateAsync({ occurrenceId: overrideTarget, reviewer: user.email, payload });
              setOverrideTarget(null);
            }} disabled={overrideEdit.isPending}>บันทึก Override</Button>
          </DialogFooter>
        </DialogContent>
              </Dialog>
            </div>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}



